import { bearingDeg, distanceM, formatDistance, playingDistance } from './geo.ts';
import { holeFrame } from './holeFrame.ts';
import type { Hole, LngLat, Weather } from './types';

/**
 * The caddy.
 *
 * A caddy does not read you a fixed table of numbers. Standing on the tee they
 * talk about the shape of the hole and what you have to carry; on the approach
 * they talk about the wind and the club; round the green they talk about the
 * ground. So these readouts are SCORED for the moment you are in, and the rails
 * show whichever ones matter most right now — the box that held the wind on the
 * tee holds the club on the approach.
 *
 * Everything here is derived from data we actually hold: the surveyed
 * centreline, hazard carry points, live Open-Meteo wind and temperature, and
 * the golfer's own bag. Where we cannot know something — green break, ball
 * tracking — it returns nothing at all rather than a confident guess. An
 * invented green read is worse than no green read: it will be trusted.
 */

export interface Readout {
  id: string;
  label: string;
  value: string;
  detail?: string;
  tone?: 'normal' | 'accent' | 'warn';
  /** Higher shows first. Scored per situation, not fixed. */
  score: number;
}

export interface Club {
  name: string;
  /** Carry in metres, the golfer's own. */
  metres: number;
}

/**
 * A starting bag, in metres, for a mid-handicap amateur.
 *
 * This is a STARTING POINT, not a claim about anybody's swing, and the readout
 * says so until the golfer edits it. Silently recommending a 7-iron based on
 * numbers we invented is exactly the sort of confident wrongness that loses
 * trust in one shot.
 */
export const DEFAULT_BAG: Club[] = [
  { name: 'Driver', metres: 210 }, { name: '3 wood', metres: 190 },
  { name: '5 wood', metres: 175 }, { name: '4 iron', metres: 160 },
  { name: '5 iron', metres: 150 }, { name: '6 iron', metres: 140 },
  { name: '7 iron', metres: 130 }, { name: '8 iron', metres: 120 },
  { name: '9 iron', metres: 110 }, { name: 'PW', metres: 95 },
  { name: 'GW', metres: 80 }, { name: 'SW', metres: 65 },
];

export interface CaddyContext {
  hole: Hole;
  teeColour: string;
  position: LngLat | null;
  weather: Weather | null;
  unit: 'm' | 'yd';
  bag?: Club[];
  /** False while the bag is still our default rather than the golfer's own. */
  bagConfirmed?: boolean;
}

const fmt = (m: number, unit: 'm' | 'yd') => Math.round(formatDistance(m, unit));

/** Where along and across the hole a point sits, in metres. */
function holeCoords(hole: Hole, teeColour: string) {
  const tee = hole.tees[teeColour] ?? Object.values(hole.tees)[0];
  const green = hole.green.middle;
  if (!tee || !green) return null;
  const project = holeFrame(tee, green);
  // y is negative up the hole; "along" reads more naturally as positive.
  return { tee, green, at: (p: LngLat) => { const q = project(p); return { along: -q.y, across: q.x }; } };
}

/**
 * Which way the hole bends, and by how much.
 *
 * Measured as the largest sideways departure of the centreline from the
 * straight tee-green line — that IS the dogleg, and it is what decides whether
 * a draw or a fade fits the hole.
 */
export function doglegOf(hole: Hole, teeColour: string): { side: 'left' | 'right' | 'straight'; metres: number } {
  const hc = holeCoords(hole, teeColour);
  if (!hc || !hole.centreline?.length) return { side: 'straight', metres: 0 };
  let worst = 0;
  for (const p of hole.centreline) {
    const { across } = hc.at(p);
    if (Math.abs(across) > Math.abs(worst)) worst = across;
  }
  // Under ~18 m of departure over a whole hole is not a shape anybody plays for.
  if (Math.abs(worst) < 18) return { side: 'straight', metres: Math.abs(worst) };
  return { side: worst < 0 ? 'left' : 'right', metres: Math.abs(worst) };
}

/** The next hazard still in front of the golfer, with its carry. */
export function nextCarry(ctx: CaddyContext) {
  const { hole, teeColour, position } = ctx;
  const hc = holeCoords(hole, teeColour);
  if (!hc) return null;
  const from = position ?? hc.tee;
  const me = hc.at(from).along;
  const ahead = hole.hazards
    .map((h) => ({ h, ...hc.at(h.point), carry: distanceM(from, h.point) }))
    .filter((x) => x.along > me + 5)
    .sort((a, b) => a.carry - b.carry);
  return ahead[0] ?? null;
}

export function caddyReadouts(ctx: CaddyContext): Readout[] {
  const { hole, teeColour, position, weather, unit } = ctx;
  const hc = holeCoords(hole, teeColour);
  if (!hc) return [];

  const from = position ?? hc.tee;
  const toGreen = distanceM(from, hole.green.middle);
  const alongMe = hc.at(from).along;
  const holeLen = distanceM(hc.tee, hole.green.middle);
  const out: Readout[] = [];

  /*
   * Where in the hole we are decides what a caddy talks about. These three
   * phases are the whole scoring model: a number that matters on the tee is
   * noise on the approach and vice versa.
   */
  const onTee = alongMe < 25;
  const nearGreen = toGreen < 35;
  const approach = !onTee && !nearGreen;

  // ---- Adjusted yardage -----------------------------------------------
  // Wind and slope change the club, which is the entire point of knowing them.
  const rise = hole.greenElevation != null && hole.teeElevation != null
    ? hole.greenElevation - hole.teeElevation
    : undefined;
  const shotBearing = bearingDeg(from, hole.green.middle);
  const adj = playingDistance({
    flatM: toGreen,
    riseM: rise,
    windSpeed: weather?.windSpeed,
    windDirFrom: weather?.windDir,
    shotBearing,
  });
  const delta = adj.metres - toGreen;
  if (Math.abs(delta) >= 2) {
    out.push({
      id: 'plays',
      label: 'Plays like',
      value: `${fmt(adj.metres, unit)}`,
      detail: `${delta > 0 ? '+' : ''}${fmt(delta, unit)} on ${fmt(toGreen, unit)}`,
      tone: 'accent',
      score: nearGreen ? 88 : 96,
    });
  }

  // ---- Wind -------------------------------------------------------------
  if (weather) {
    // Relative angle decides the words: a caddy says "into off the left", not
    // "wind bearing 214 degrees".
    const rel = (((weather.windDir - shotBearing) % 360) + 540) % 360 - 180;
    const abs = Math.abs(rel);
    const face = abs < 45 ? 'into' : abs > 135 ? 'behind' : rel < 0 ? 'off the right' : 'off the left';
    out.push({
      id: 'wind',
      label: 'Wind',
      value: `${Math.round(weather.windSpeed)} km/h`,
      detail: `${face}${adj.windAdj ? ` · ${adj.windAdj > 0 ? '+' : ''}${fmt(adj.windAdj, unit)}` : ''}`,
      tone: weather.windSpeed >= 25 ? 'warn' : 'normal',
      // Wind matters most when there is a shot to the green in hand.
      score: (approach ? 90 : 74) + Math.min(weather.windSpeed, 30) / 3,
    });
  }

  // ---- Elevation --------------------------------------------------------
  if (rise != null && Math.abs(rise) >= 2) {
    out.push({
      id: 'elevation',
      label: 'Elevation',
      value: `${rise > 0 ? '+' : ''}${Math.round(rise)} m`,
      detail: rise > 0 ? 'uphill' : 'downhill',
      score: 70,
    });
  }

  // ---- Carry ------------------------------------------------------------
  const carry = nextCarry(ctx);
  if (carry) {
    const reachable = carry.carry < 250;
    out.push({
      id: 'carry',
      label: `${carry.h.kind} carry`,
      value: `${fmt(carry.carry, unit)}`,
      detail: carry.across < -8 ? 'left' : carry.across > 8 ? 'right' : 'centre',
      tone: carry.h.kind === 'water' ? 'warn' : 'normal',
      // A carry is the tee shot's whole question, and irrelevant once past it.
      score: (onTee ? 95 : 78) + (reachable ? 6 : 0),
    });
  }

  // ---- Shot shape -------------------------------------------------------
  const dog = doglegOf(hole, teeColour);
  if (onTee || alongMe < holeLen * 0.4) {
    out.push({
      id: 'shape',
      label: 'Shot shape',
      value: dog.side === 'left' ? 'Draw' : dog.side === 'right' ? 'Fade' : 'Straight',
      detail: dog.side === 'straight'
        ? 'no shape needed'
        : `dogleg ${dog.side} ${Math.round(dog.metres)} m`,
      // The shape question is asked standing on the tee and nowhere else.
      score: onTee ? 92 : 60,
    });
  }

  // ---- Miss margin ------------------------------------------------------
  /*
   * The good miss: whichever side of the corridor is NOT where the trouble is,
   * over the stretch you are about to hit into. Derived from hazard positions
   * rather than asserted, and withheld when both sides are equally guarded —
   * "miss anywhere" is not advice.
   */
  const zoneStart = alongMe;
  const zoneEnd = alongMe + Math.min(220, Math.max(60, toGreen));
  const inZone = hole.hazards
    .map((h) => ({ h, ...hc.at(h.point) }))
    .filter((x) => x.along >= zoneStart && x.along <= zoneEnd);
  if (inZone.length) {
    const left = inZone.filter((x) => x.across < -5).length;
    const right = inZone.filter((x) => x.across > 5).length;
    if (left !== right) {
      out.push({
        id: 'miss',
        label: 'Good miss',
        value: left > right ? 'Right' : 'Left',
        detail: `trouble ${left > right ? 'left' : 'right'}`,
        score: onTee ? 80 : 84,
      });
    }
  }

  // ---- Hidden trouble ---------------------------------------------------
  /*
   * Blind hazards: past the bend and off the line you are looking down, so the
   * golfer cannot see them from here. Only raised while they are still in
   * front — a bunker you have walked past is not a warning.
   */
  if (dog.side !== 'straight') {
    const apex = Math.max(...(hole.centreline ?? []).map((p) => Math.abs(hc.at(p).across)), 0);
    const blind = hole.hazards
      .map((h) => ({ h, ...hc.at(h.point) }))
      .filter((x) => x.along > alongMe + 40 && Math.abs(x.across) > Math.max(apex * 0.6, 20));
    if (blind.length) {
      out.push({
        id: 'blind',
        label: 'Hidden',
        value: `${blind.length} ${blind.length === 1 ? 'hazard' : 'hazards'}`,
        detail: `past the ${dog.side} bend`,
        tone: 'warn',
        score: onTee ? 76 : 66,
      });
    }
  }

  // ---- Club -------------------------------------------------------------
  const bag = ctx.bag?.length ? ctx.bag : DEFAULT_BAG;
  if (!onTee || holeLen < 240) {
    // Against the PLAYING distance, not the flat one — clubbing off the raw
    // number is the mistake wind and slope readouts exist to prevent.
    const want = adj.metres;
    const pick = [...bag].sort((a, b) => Math.abs(a.metres - want) - Math.abs(b.metres - want))[0];
    if (pick) {
      out.push({
        id: 'club',
        label: 'Club',
        value: pick.name,
        detail: ctx.bagConfirmed
          ? `your ${fmt(pick.metres, unit)}`
          : 'default bag — set yours',
        tone: ctx.bagConfirmed ? 'accent' : 'normal',
        score: approach ? 94 : nearGreen ? 72 : 58,
      });
    }
  }

  // ---- Green reads / ball tracking --------------------------------------
  // Deliberately absent. Break needs green contours we have not surveyed, and
  // ball tracking needs the ball. Both are listed in the caddy brief; neither
  // can be answered honestly yet, so neither is shown.

  return out.sort((a, b) => b.score - a.score);
}
